import { sanitizeHtml } from '../../../common/functions/sanitize-html';
import { isEmptyTiptapHtml } from '../is-empty-content';

export interface ExportRichTextToPdfOptions {
  /**
   * Document title. Written to the print document's `<title>`, which most browsers use as the
   * default "Save as PDF" filename; also used as the file name when exporting via the mobile
   * native bridge. Defaults to "document".
   */
  title?: string;
  /** `@page` margin for the printed output (any CSS margin value). Defaults to "20mm". */
  margin?: string;
  /**
   * How long (ms) to wait for images to finish loading before printing anyway. Remote images that
   * never resolve should not block the dialog forever. Defaults to 3000. (Browser path only.)
   */
  imageLoadTimeoutMs?: number;
}

interface ReactNativeWebViewBridge {
  postMessage(data: string): void;
}

const DEFAULT_MARGIN = '20mm';
const DEFAULT_IMAGE_TIMEOUT_MS = 3000;
const CLEANUP_BACKSTOP_MS = 60_000;
const MAX_PDF_FILENAME_LENGTH = 80;

/**
 * One to four CSS length/percentage values (or `0`/`auto`). The only shape accepted for the caller-supplied page `margin`.
 */
const SAFE_MARGIN = /^(?:\s*(?:0|(?:\d+|\d*\.\d+)(?:px|pt|pc|in|cm|mm|q|em|rem|ex|%)|auto)){1,4}\s*$/i;

/**
 * Guards `options.margin` before it is interpolated into a `<style>` tag. `margin` is a public-API
 * option, so a malformed or hostile value (e.g. `0 } body { … }`) could otherwise inject arbitrary
 * CSS or break out of the style element. Falls back to the default when the value isn't a plain CSS margin.
 */
const safeMargin = (margin: string | undefined): string =>
  margin !== undefined && SAFE_MARGIN.test(margin) ? margin.trim() : DEFAULT_MARGIN;

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const getReactNativeWebView = (): ReactNativeWebViewBridge | undefined =>
  (window as Window & { ReactNativeWebView?: ReactNativeWebViewBridge }).ReactNativeWebView;

/**
 * Sanitizes a title for use as a PDF filename, removing path separators and reserved characters
 * while preserving readability. Removes leading/trailing dots to prevent hidden files.
 * Used internally by both web and mobile paths to ensure consistent filenames across platforms.
 */
const sanitizeForPdfFileName = (title: string): string => {
  const base = title
    .replace(/\.pdf$/i, '') // strip an existing extension
    .replace(/[\\/:*?"<>|]+/g, ' ') // path separators + reserved chars -> space
    .replace(/\s+/g, ' ')
    .replace(/^[.\s]+|[.\s]+$/g, '') // no leading/trailing dots or whitespace
    .slice(0, MAX_PDF_FILENAME_LENGTH)
    .trim();
  return base || 'document';
};

/**
 * Builds the `.pdf` file name sent to the mobile native bridge, where it becomes a file on disk.
 * Uses sanitization consistent with the web path to ensure uniform filenames across platforms.
 */
const toPdfFileName = (title: string): string => {
  return `${sanitizeForPdfFileName(title)}.pdf`;
};

const MAX_DERIVED_TITLE_LENGTH = 60;

/**
 * Derives a human file name from the content itself when the caller gives no explicit title.
 * Uses the first heading/block's text, falling back to all text, capped to a sane length. Used so the
 * browser's "Save as PDF" filename reflects the document instead of the host page title. Returns
 * "document" when the content has no usable text. Operates on already-sanitised HTML.
 */
const deriveFileTitle = (sanitizedHtml: string): string => {
  const container = document.createElement('div');
  container.innerHTML = sanitizedHtml;
  const firstBlock = container.querySelector('h1, h2, h3, h4, h5, h6, p, li, blockquote, td, th');
  const text = (firstBlock?.textContent ?? container.textContent ?? '').replace(/\s+/g, ' ').trim();
  if (!text) {
    return 'document';
  }
  return text.length > MAX_DERIVED_TITLE_LENGTH ? text.slice(0, MAX_DERIVED_TITLE_LENGTH).trim() : text;
};

/**
 * Print-specific rules layered on top of the app stylesheets. Keeps the on-screen `.rich-text`
 * typography but adapts it for paged output: full-width images, page margins, and sensible break
 * hints so headings/tables/code blocks are not split awkwardly. (Block spacing comes from the
 * shared `.rich-text` rules in rich-text.css, which are copied into the frame.)
 */
const buildPrintStyles = (margin: string, { bodyPadding = false }: { bodyPadding?: boolean } = {}): string => `
  ${
    bodyPadding
      ? `@page { margin: 0; } html { margin: 0; padding: 0; background: #ffffff; } body { margin: 0; padding: ${margin}; background: #ffffff; }`
      : `@page { margin: ${margin}; } html, body { margin: 0; padding: 0; background: #ffffff; }`
  }
  /* Browsers drop background colours when printing; force them so highlight marks and table
     header shading render in the PDF the same way they do on screen. */
  .rich-text, .rich-text * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .rich-text { max-width: none; }
  .rich-text img { max-width: 100% !important; height: auto; }
  .rich-text h1, .rich-text h2, .rich-text h3 { break-after: avoid; }
  .rich-text pre, .rich-text blockquote, .rich-text table, .rich-text img { break-inside: avoid; }
  .rich-text tr, .rich-text li { break-inside: avoid; }
  .rich-text pre { overflow-x: visible !important; white-space: pre-wrap !important; word-wrap: break-word !important; font-size: 11px !important; }
`;

/**
 * Copies the host document's stylesheets so the print frame renders `.rich-text` content exactly
 * as the read-only viewer does. Cloned by serialising the node (not by reading `cssText`) so
 * cross-origin `<link>` sheets are simply re-fetched inside the frame rather than triggering a
 * SecurityError. A `<base>` is injected (by the caller) so relative hrefs resolve against the host.
 */
const collectHeadStyles = (): string =>
  Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
    .map((node) => node.outerHTML)
    .join('\n');

/**
 * Serialises the host document's CSS rules into inline `<style>` blocks.
 * Used for the mobile PDF export path, where the native renderer can't reach the bundled stylesheets.
 * The exported root forces light mode (see `buildDocumentHtml`), so selector-based dark rules can't
 * match; `prefers-color-scheme: dark` media still tracks the device, so those blocks are dropped here.
 * Prioritises `:root` rules first so CSS variables are defined before they're used.
 */
const serializeStyleSheets = (): string => {
  const blocks: string[] = [];
  const variableRules: string[] = [];

  for (const sheet of Array.from(document.styleSheets)) {
    try {
      let css = '';
      for (const rule of Array.from(sheet.cssRules)) {
        const styleRule = rule as CSSStyleRule;

        // `color-scheme: light` and `data-color-scheme="light"` on the root neutralise selector-based
        // dark rules, but not `prefers-color-scheme: dark` media (it tracks the device), so drop those.
        if ((rule as CSSMediaRule).media?.mediaText?.includes('prefers-color-scheme: dark')) {
          continue;
        }

        // Extract :root rules separately to ensure variable definitions come first
        if (styleRule.selectorText === ':root') {
          variableRules.push(rule.cssText);
        } else {
          css += rule.cssText;
        }
      }
      if (css) {
        blocks.push(`<style>${css}</style>`);
      }
    } catch {
      // Cross-origin stylesheet. cssRules access is denied; skip it.
    }
  }

  // Inject variable definitions first so they're available when other rules use them
  if (variableRules.length > 0) {
    return `<style>${variableRules.join(' ')}</style>\n${blocks.join('\n')}`;
  }

  return blocks.join('\n');
};

const buildDocumentHtml = ({
  title,
  margin,
  headStyles,
  bodyHtml,
}: {
  title: string;
  margin: string;
  headStyles: string;
  bodyHtml: string;
}): string =>
  // Force light mode regardless of the device theme, mirroring `applyColorScheme('light')`:
  // inline `color-scheme: light` re-resolves the design system's `light-dark()` tokens to their
  // light value (beating the copied `:root { color-scheme: light dark }` on specificity), and
  // `data-color-scheme="light"` neutralises the selector-based `dark` variant from global.css. The
  // `color-scheme` meta additionally opts the document out of Android WebView's algorithmic darkening.
  `<!doctype html><html lang="${escapeHtml(document.documentElement.lang || 'en')}" data-color-scheme="light" style="color-scheme: light;"><head>` +
  `<meta charset="utf-8"><meta name="color-scheme" content="light"><base href="${escapeHtml(document.baseURI)}">` +
  `<title>${escapeHtml(title)}</title>${headStyles}<style>${buildPrintStyles(margin, { bodyPadding: true })}</style>` +
  `</head><body>${bodyHtml}</body></html>`;

/**
 * Resolves once every image in the frame has settled (loaded or errored), or once `timeoutMs`
 * elapses, whichever comes first. Printing before images load yields blank boxes.
 */
const waitForImages = (doc: Document, timeoutMs: number): Promise<void> => {
  const pending = Array.from(doc.images).filter((img) => !img.complete);
  if (pending.length === 0) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    let remaining = pending.length;
    let settled = false;

    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      resolve();
    };

    const onSettle = () => {
      remaining -= 1;
      if (remaining <= 0) {
        finish();
      }
    };

    const timer = setTimeout(finish, timeoutMs);
    for (const img of pending) {
      img.addEventListener('load', onSettle, { once: true });
      img.addEventListener('error', onSettle, { once: true });
    }
  });
};

/**
 * Prefetches images with authentication headers and converts them to data URIs,
 * ensuring they render in the print iframe. This solves cross-origin API images
 * that fail to load when fetched from the iframe without auth context.
 */
const prefetchImagesAsDataUris = async (html: string, timeoutMs: number): Promise<string> => {
  const container = document.createElement('div');
  container.innerHTML = html;
  const images = Array.from(container.querySelectorAll('img[src]'));

  if (images.length === 0) {
    return html;
  }

  await Promise.all(
    images.map(async (img) => {
      const src = img.getAttribute('src');
      if (!src) return;

      try {
        // Fetch with credentials: 'include' so the browser includes cookies/auth.
        // If a Service Worker is installed, it intercepts and adds necessary headers.
        const response = await Promise.race([
          fetch(src, { credentials: 'include' }),
          new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Image fetch timeout')), timeoutMs)),
        ]);

        if (!response.ok) {
          return;
        }

        const blob = await response.blob();
        const reader = new FileReader();

        const dataUri = await new Promise<string>((resolve) => {
          reader.onload = () => {
            resolve(reader.result as string);
          };
          reader.readAsDataURL(blob);
        });

        img.setAttribute('src', dataUri);
      } catch {
        // Print proceeds with broken image placeholders.
      }
    })
  );

  return container.innerHTML;
};

/**
 * Forces already-loaded images to finish decoding before printing.
 */
const decodeImages = async (doc: Document): Promise<void> => {
  await Promise.all(
    Array.from(doc.images).map(async (img) => {
      if (typeof img.decode !== 'function' || !img.complete) {
        return;
      }
      try {
        await img.decode();
      } catch {
        // Decode can reject for a broken image. Print proceeds with whatever rendered.
      }
    })
  );
};

/**
 * Exports rich-text HTML (the `editor.getHTML()` / stored content string) to PDF.
 *
 * In a browser, renders the sanitised content in an isolated, off-screen iframe styled with the
 * app's own stylesheets, then triggers the print dialog so the user can "Save as PDF".
 *
 * Inside the mobile app's WebView (where `window.print()` does nothing) it instead posts a
 * self-contained `EXPORT_PDF` document to the native layer, which renders and shares the PDF.
 *
 * No-ops on empty content.
 */
export const exportRichTextToPdf = async (html: string, options: ExportRichTextToPdfOptions = {}): Promise<void> => {
  if (typeof document === 'undefined' || isEmptyTiptapHtml(html)) {
    return;
  }

  const { imageLoadTimeoutMs = DEFAULT_IMAGE_TIMEOUT_MS } = options;
  const margin = safeMargin(options.margin);
  const sanitized = sanitizeHtml(html);
  const bodyHtml = `<div class="rich-text">${sanitized}</div>`;
  const title = options.title?.trim() || deriveFileTitle(sanitized);

  const bridge = getReactNativeWebView();
  if (bridge) {
    const documentHtml = buildDocumentHtml({
      title,
      margin,
      headStyles: serializeStyleSheets(),
      bodyHtml,
    });
    bridge.postMessage(JSON.stringify({ type: 'EXPORT_PDF', html: documentHtml, fileName: toPdfFileName(title) }));
    return;
  }

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('tabindex', '-1');
  iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:794px;height:1123px;border:0;';
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  const frameWindow = iframe.contentWindow;
  if (!doc || !frameWindow) {
    iframe.remove();
    return;
  }

  const bodyHtmlWithCachedImages = await prefetchImagesAsDataUris(bodyHtml, imageLoadTimeoutMs);

  doc.documentElement.lang = document.documentElement.lang || 'en';
  // Force light mode, mirroring `applyColorScheme('light')`: inline `color-scheme: light` re-resolves
  // the `light-dark()` tokens (beating the copied `:root { color-scheme: light dark }` on specificity),
  // and `data-color-scheme="light"` neutralises the selector-based `dark` variant; the meta opts out
  // of WebView force-dark.
  doc.documentElement.style.colorScheme = 'light';
  doc.documentElement.setAttribute('data-color-scheme', 'light');
  doc.head.innerHTML = `<meta name="color-scheme" content="light"><base href="${escapeHtml(
    document.baseURI
  )}"><title>${escapeHtml(title)}</title>${collectHeadStyles()}<style>${buildPrintStyles(margin)}</style>`;
  doc.body.innerHTML = bodyHtmlWithCachedImages;

  await waitForImages(doc, imageLoadTimeoutMs);
  await decodeImages(doc);

  // Browsers derive the "Save as PDF" filename from the *host* document's title, not the frame's,
  // so swap it for the duration of the dialog and restore it on cleanup. Pre-sanitize the title
  // to ensure the web path produces the same filename as the mobile path.
  const previousTitle = document.title;
  document.title = sanitizeForPdfFileName(title);

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) {
      return;
    }
    cleaned = true;
    clearTimeout(backstop);
    document.title = previousTitle;
    iframe.remove();
  };

  // `print()` is synchronous in some browsers and async in others. `afterprint` covers both.
  // The backstop guarantees the iframe is eventually removed even if `afterprint` never fires.
  const backstop = setTimeout(cleanup, CLEANUP_BACKSTOP_MS);
  frameWindow.addEventListener('afterprint', cleanup, { once: true });

  try {
    frameWindow.focus();
    frameWindow.print();
  } catch (error) {
    cleanup();
    throw error;
  }
};
